import React, { useState } from 'react';
import PriceBreakdownModal from './PriceBreakdownModal';
import { Wallet as WalletIcon } from 'lucide-react';

/**
 * PriceBreakdownButton - Button component that opens the price breakdown modal
 * 
 * @param {Object} props
 * @param {string} props.entityType - Type of entity ('roomType', 'course', 'addOnService')
 * @param {string} props.entityId - ID of the entity
 * @param {Object} props.entity - The entity object (with name, basePrice, etc.)
 * @param {Function} props.onUpdate - Callback after breakdowns are updated
 * @param {string} props.variant - Button style variant ('primary', 'secondary', 'icon')
 * @param {string} props.size - Button size ('small', 'medium', 'large')
 */
function PriceBreakdownButton({ 
  entityType, 
  entityId, 
  entity, 
  onUpdate,
  variant = 'primary',
  size = 'medium'
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getButtonStyles = () => {
    const baseStyles = {
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '600',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s ease'
    };

    // Size styles
    const sizeStyles = {
      small: {
        padding: '6px 12px',
        fontSize: '12px'
      },
      medium: {
        padding: '10px 16px',
        fontSize: '14px'
      },
      large: {
        padding: '12px 20px',
        fontSize: '16px'
      }
    };

    // Variant styles
    const variantStyles = {
      primary: {
        backgroundColor: '#1A3E2A',
        color: 'white'
      },
      secondary: {
        backgroundColor: '#1A3E2A',
        color: 'white'
      },
      icon: {
        backgroundColor: 'transparent',
        color: '#1A3E2A',
        border: '1px solid #1A3E2A',
        padding: sizeStyles[size].padding
      }
    };

    return {
      ...baseStyles,
      ...sizeStyles[size],
      ...variantStyles[variant]
    };
  };

  const getButtonContent = () => {
    if (variant === 'icon') {
      return (
        <>
          <span style={{ fontSize: '16px' }}><WalletIcon size="1em" aria-hidden="true" /></span>
          <span>Breakdowns</span>
        </>
      );
    }

    return (
      <>
        <span><WalletIcon size="1em" aria-hidden="true" /></span>
        <span>Price Breakdowns</span>
      </>
    );
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        style={getButtonStyles()}
        onMouseOver={(e) => {
          if (variant === 'primary') e.currentTarget.style.backgroundColor = '#163626';
          if (variant === 'secondary') e.currentTarget.style.backgroundColor = '#163626';
          if (variant === 'icon') e.currentTarget.style.borderColor = '#163626';
        }}
        onMouseOut={(e) => {
          if (variant === 'primary') e.currentTarget.style.backgroundColor = '#1A3E2A';
          if (variant === 'secondary') e.currentTarget.style.backgroundColor = '#1A3E2A';
          if (variant === 'icon') e.currentTarget.style.borderColor = '#1A3E2A';
        }}
        title="Manage price breakdowns"
      >
        {getButtonContent()}
      </button>

      <PriceBreakdownModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        entityType={entityType}
        entityId={entityId}
        entity={entity}
        onUpdate={() => {
          if (onUpdate) {
            onUpdate();
          }
        }}
      />
    </>
  );
}

export default PriceBreakdownButton;
